/**
 * Indexavtal — Version 2026 (indexavtal v1).
 *
 * Bilaga-only document under a lokalhyresavtal; no signing of its own.
 * Legal text verbatim from `base-templates/Mall_lokalhyresavtal_Index.docx`
 * (approved by Haymanot Tesfaye). DO NOT paraphrase or reorder.
 */
import { em, option, optionList, p, section, t, val } from '@hyranai/regta-document-ir';
import { customSectionsBlockDefinition, fieldsSection, formatSlotNumber, optionChosen, selectSlot, shown, slotNumber, slotText, textVal, ticked, whenOption, } from '../common/index.js';
const K = 'indexavtal';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot Tesfaye', source: 'base-templates/Mall_lokalhyresavtal_Index.docx' },
};
export const ATTACHMENT_TYPE_OPTIONS = [
    { code: 'appendix', value: 'bilaga', label: 'Bilaga till avtal (kompletterar avtalet)', summary: 'Attached as an appendix that supplements the contract.' },
    { code: 'amendment', value: 'tillägg', label: 'Tillägg till avtal (ändrar villkor)', summary: 'Attached as an amendment that changes the contract terms.' },
];
const [BILAGA, TILLAGG] = ATTACHMENT_TYPE_OPTIONS;
export const INDEX_CLAUSE_MODE_OPTIONS = [
    { code: 'standard_kpi', value: 'standard', label: 'Standardklausul (KPI, basår 2020)', summary: 'Use the standard CPI index clause (base year 2020).' },
    { code: 'manual_freetext', value: 'manual', label: 'Helt manuell input (fritext)', summary: 'Write the entire index-adjustment clause by hand as free text.' },
];
const [STANDARD, MANUAL] = INDEX_CLAUSE_MODE_OPTIONS;
export const BASE_RENT_MODE_OPTIONS = [
    { code: 'amount', value: 'amount', label: 'Belopp (kr)', summary: 'Base rent is given as a SEK amount.' },
    { code: 'percent', value: 'percent', label: 'Procent av angiven hyra', summary: 'Base rent is given as a percentage of the stated rent.' },
];
const [AMOUNT, PERCENT] = BASE_RENT_MODE_OPTIONS;
const FROM_PARENT = 'Hämtas från lokalhyresavtalet.';
/**
 * The indexavtal has no parties of its own (a bilaga under the
 * lokalhyresavtal): its party details are overlaid from the parent
 * contract's same-named values (`derivedFromParent`), as in the legacy lane.
 */
const partySection = (who) => {
    const slot = (key, label) => ({
        key: `${who}_${key}`,
        label,
        type: 'text',
        required: true,
        derivedFromParent: true,
        help: FROM_PARENT,
    });
    return fieldsSection({
        blockKey: `${K}.${who === 'landlord' ? 'hyresvard' : 'hyresgast'}`,
        ref: who === 'landlord' ? 'hyresvard' : 'hyresgast',
        title: who === 'landlord' ? 'Hyresvärd' : 'Hyresgäst',
        summary: `The ${who}: name, organisation number and postal address, carried over from the lokalhyresavtal.`,
        rows: [
            { slot: slot('name', 'Namn') },
            { printed: 'Org.nr', slot: slot('org_number', 'Organisationsnummer') },
            { slot: slot('address', 'Gatuadress') },
            { printed: 'Postadress', slot: slot('postal_address', 'Postnummeradress') },
        ],
    });
};
export const hyresvard = partySection('landlord');
export const hyresgast = partySection('tenant');
export const avtalskoppling = {
    ...LEGAL,
    blockKey: `${K}.avtalskoppling`,
    version: 'v1',
    title: 'Avtalskoppling',
    summary: 'Whether the index agreement amends or supplements the lokalhyresavtal, and that contract’s number.',
    slots: [
        selectSlot('attachment_type', 'Typ av koppling', ATTACHMENT_TYPE_OPTIONS, {
            required: true,
            default: BILAGA.code,
            help: '"Tillägg" ändrar villkoren i lokalhyresavtalet (vanligast för ' +
                'indexering). "Bilaga" kompletterar avtalet utan att ändra det.',
        }),
        {
            key: 'parent_contract_number',
            label: 'Avtalsnummer',
            type: 'text',
            required: true,
            derivedFrom: 'contract_number',
            help: 'Hämtas från lokalhyresavtalets avtalsnummer.',
        },
    ],
    compile: (input, ctx) => {
        const line = (lead) => [t(lead), textVal(input, 'parent_contract_number')];
        return [
            section('avtalskoppling', 'Avtalskoppling', [
                optionList([
                    option(ticked(ctx, optionChosen(ATTACHMENT_TYPE_OPTIONS, input.attachment_type, TILLAGG)), line('Tillägg till avtal nr: '), { code: TILLAGG.code }),
                    option(ticked(ctx, optionChosen(ATTACHMENT_TYPE_OPTIONS, input.attachment_type, BILAGA)), line('Bilaga till avtal nr: '), { code: BILAGA.code }),
                ], { sourceKey: 'attachment_type', select: 'single' }),
            ]),
        ];
    },
};
const amountWithUnit = (ctx, input, key, unit) => {
    const n = slotNumber(input[key]);
    const text = formatSlotNumber(ctx, n);
    return text == null
        ? [val(null, { sourceKey: key, blankWidth: 'short' }), t(` ${unit}`)]
        : [val(`${text} ${unit}`, { raw: n, format: 'number', sourceKey: key })];
};
export const indexreglering = {
    ...LEGAL,
    blockKey: `${K}.indexreglering`,
    version: 'v1',
    title: 'Indexreglering',
    summary: 'The KPI index clause (base year 2020) with its base rent and base index, or a fully hand-written clause.',
    slots: [
        selectSlot('index_clause_mode', 'Indexregleringsklausul', INDEX_CLAUSE_MODE_OPTIONS, {
            required: true,
            default: STANDARD.code,
            help: 'Standardklausulen använder den legala KPI-formuleringen. Välj "Helt ' +
                'manuell input" för att skriva hela indexregleringsklausulen för hand.',
        }),
        {
            key: 'index_manual_text',
            label: 'Indexregleringsklausul (fritext)',
            type: 'textarea',
            required: true,
            visibleWhen: whenOption('index_clause_mode', MANUAL),
            help: 'Skriv hela indexregleringsklausulen. Ersätter standardklausulen i avtalet.',
        },
        selectSlot('base_rent_mode', 'Bashyran anges som', BASE_RENT_MODE_OPTIONS, {
            required: true,
            default: AMOUNT.code,
            visibleWhen: { not: whenOption('index_clause_mode', MANUAL) },
        }),
        {
            key: 'base_rent_amount',
            label: 'Bashyra (kr)',
            type: 'currency',
            min: 0,
            required: true,
            derivedFrom: 'rent_amount',
            visibleWhen: { all: [{ not: whenOption('index_clause_mode', MANUAL) }, { not: whenOption('base_rent_mode', PERCENT) }] },
            help: 'Hämtas från lokalhyresavtalets hyra. Lås upp för att ange en avvikande bashyra.',
        },
        {
            key: 'base_rent_percent',
            label: 'Bashyra (% av hyran)',
            type: 'number',
            min: 0,
            max: 100,
            required: true,
            visibleWhen: { all: [{ not: whenOption('index_clause_mode', MANUAL) }, whenOption('base_rent_mode', PERCENT)] },
            help: 'Anges utan procenttecken. T.ex. "100" = hela hyran.',
        },
        {
            key: 'base_year_override',
            label: 'Avvikande bastal — år',
            type: 'text',
            visibleWhen: { not: whenOption('index_clause_mode', MANUAL) },
            help: 'Lämnas tomt om standardregeln gäller (avtal som börjar 1/1–30/6 → ' +
                'oktober året innan; 1/7–31/12 → oktober samma år). Fyll i endast ' +
                'om annat bastal ska gälla. Ange enbart årtal (t.ex. "2024").',
        },
    ],
    compile: (input, ctx) => {
        const isManual = optionChosen(INDEX_CLAUSE_MODE_OPTIONS, input.index_clause_mode, MANUAL);
        const isPercent = optionChosen(BASE_RENT_MODE_OPTIONS, input.base_rent_mode, PERCENT);
        const showAmount = shown(ctx, !isPercent);
        const showPercent = shown(ctx, isPercent);
        const override = slotText(input.base_year_override);
        const showOverride = shown(ctx, override != null);
        const content = [];
        if (shown(ctx, isManual))
            content.push(p([textVal(input, 'index_manual_text', 'long')]));
        if (shown(ctx, !isManual)) {
            content.push(p([
                t('Av det i kontraktet angivna hyresbeloppet ska '),
                ...(showAmount ? amountWithUnit(ctx, input, 'base_rent_amount', 'kr') : []),
                ...(showAmount && showPercent ? [t(' eller ')] : []),
                ...(showPercent ? amountWithUnit(ctx, input, 'base_rent_percent', '%') : []),
                t(' utgöra bashyra. Under hyrestiden ska med hänsyn till förändringarna i konsumentprisindex (totalindex med 2020 som basår) tillägg till hyresbeloppet utgå med en viss procent av bashyran enligt nedanstående grunder.'),
            ]), p([
                t('För hyresavtal som börjar löpa någon gång under tiden 1/1–30/6 anses bashyran anpassad till indextalet för oktober månad året innan. För hyresavtal som börjar löpa någon gång under tiden 1/7–31/12 anses bashyran i stället anpassad till indextalet för oktober månad under samma tid. Indextalet för den oktober månad till vilken bashyran enligt ovan anses anpassad utgör bastal såvida inte annat avtalats'),
                ...(showOverride ? [t(': indextalet för oktober månad år '), textVal(input, 'base_year_override', 'short')] : []),
                t('.'),
            ]), p('Skulle indextalet någon påföljande oktober månad ha stigit i förhållande till bastalet, ska tillägg utgå med det procenttal varmed indextalet ändrats i förhållande till bastalet. I fortsättningen ska tillägg utgå i förhållande till indexändringarna, varvid hyresförändringen beräknas på basis av den procentuella förändringen mellan bastalet och indextalet för respektive oktober månad. Tillägget kan bli såväl högre som lägre mellan olika år. Utgående hyra ska dock aldrig sättas lägre än det i kontraktet angivna hyresbeloppet. Hyresändringen sker alltid fr.o.m. den 1 januari efter det att oktoberindex föranlett omräkning.'));
            // The 335,92 divisor is specific to basår 2020; a custom bastal suppresses it (IX-3).
            if (shown(ctx, override == null)) {
                content.push(p('Om bastalet utgörs av indextal för månad med fastställt indextal med basår 1980 (oktober månad t.o.m. år 2025) ska indextalet omräknas till index med basår 2020 genom att bastalet divideras med medeltalet av KPI för 2020 (335,92 med basår 1980) och kvoten multipliceras med 100, avrundat till två decimaler.'));
            }
        }
        return [section('indexreglering', 'Indexreglering', content)];
    },
};
export const signeringsnotis = {
    ...LEGAL,
    blockKey: `${K}.signeringsnotis`,
    version: 'v1',
    title: 'Underskrift',
    summary: 'States that the index agreement is part of the lokalhyresavtal and is signed with it, not on its own.',
    slots: [],
    compile: () => [
        p([
            em('Detta indexavtal utgör en del av lokalhyresavtalet och undertecknas elektroniskt tillsammans med det. Egen underskrift krävs inte på denna handling.'),
        ], { variant: 'fine_print' }),
    ],
};
export const customSections = customSectionsBlockDefinition(`${K}.custom-sections`);
export const INDEXAVTAL_BLOCKS = [hyresvard, hyresgast, avtalskoppling, indexreglering, signeringsnotis, customSections];
//# sourceMappingURL=blocks.js.map